/**
 * Jacob - 19/02/2020
 * Common module for all async calls
 */

import { IAppProps } from '../interface/IAppProvider';
import { getDefaultOptions, HTTP_METHOD } from './GetAsyncOptions';
import {
  IBuyInsurance,
  EMAIL_EVENT,
  ICreatePackageResponse,
  IGeneralCallMeEmailTemplate,
  ISalesCompleteEmail,
  PRODUCTKEY
} from '../interface/IPayload';
import { TDispatch } from '../context/AppContext';

//Handle HTTP related errors
function handleHTTPErrors(response: Response, props: IAppProps, dispatch: TDispatch, options?: RequestInit) {
  if (response.ok) {
    dispatch({ isLoading: false });
    return response
      .clone()
      .json()
      .catch(() => response.text());
  } else {
    return response
      .clone()
      .json()
      .catch(() => response.text())
      .then((jsonResponse) => {
        if (jsonResponse.errorCode === 20 && jsonResponse.message) {
          props.c9log.error(`handleHTTPErrors: Error Payload from server -', ${JSON.stringify(jsonResponse)}`);
          dispatch({ emailEvent: EMAIL_EVENT.GENERAL_ERROR, prizeZero: true, stateChange: true });
        } else if (jsonResponse.errorCode === 10 && jsonResponse.message) {
          if (jsonResponse.message === 'Travel insurance already exist') {
            props.c9log.info(`handleHTTPErrors: Error Payload from server -', ${JSON.stringify(jsonResponse)}`);
            dispatch({ emailEvent: EMAIL_EVENT.HAS_EXISTING_TRAVEL });
          } else if (jsonResponse.message === 'Customers age is not valid') {
            props.c9log.info(`handleHTTPErrors: Error Payload from server -', ${JSON.stringify(jsonResponse)}`);
            dispatch({ hasAgeError: true });
          } else {
            props.c9log.error(`handleHTTPErrors: Error Payload from server -', ${JSON.stringify(jsonResponse)}`);
          }
          dispatch({ stateChange: true });
        } else if (typeof jsonResponse === 'string' && /(RegNo|AccountNo)/.test(jsonResponse)) {
          // it an error from validation of bank data, regNumber and accountNumber
          return "RegNo and accountNo don't match";
        } else {
          const responseData = `${response.status} ${response.statusText} ${response.type} ${JSON.stringify(options)}`;
          props.c9log.error(`handleHTTPErrors: Called ${response.url} Response: ${responseData}`, 'AsyncCalls');
        }
        routeToErrorPage(props, dispatch);
      });
  }
}

function handleGeneralErrors(serviceEnPoint: string, error: any, props: IAppProps, dispatch: TDispatch) {
  props.c9log.error(`handleGeneralErrors: Called ${serviceEnPoint} - error: ${error}`, 'AsyncCalls');
  routeToErrorPage(props, dispatch);
}

// Goto error url
export function routeToErrorPage(props: IAppProps, dispatch: TDispatch) {
  dispatch({ isLoading: false, hasError: { stepOne: true, stepTwo: false } });
}

// Quote GET request Query Param Generator
const getQuotesParamGenerator = (param: ICreatePackageResponse): string => {
  if (param && param.packages) {
    const quoteIDList = param.packages.map((item) => item.quoteId);
    return `quoteid=${quoteIDList && quoteIDList.join('&quoteid=')}`;
  } else {
    return '';
  }
};

// Create package Service
export const createPackage = (
  props: IAppProps,
  dispatch: TDispatch,
  body: object,
  isLoggedOn: boolean
): Promise<Response> => {
  dispatch({ isLoading: true });
  const options: RequestInit = getDefaultOptions(HTTP_METHOD.POST, props.config, 'wso2', body);
  const useApi = isLoggedOn ? props.config.wso2ApiUrl : props.config.wso2OpenApiUrl;
  return fetch(`${useApi}/packages`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages`, err, props, dispatch);
    }
  );
};

// Update package API
export const updatePackage = (props: IAppProps, dispatch: TDispatch, body: any, useCpr: boolean): Promise<Response> => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.PUT, props.config, 'wso2', body);
  const useApi = useCpr ? props.config.wso2OpenApiUrl : props.config.wso2ApiUrl;
  return fetch(`${useApi}/packages`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages`, err, props, dispatch);
    }
  );
};

// Get quotes for Packages Screen
export const getQuotes = (
  props: IAppProps,
  dispatch: TDispatch,
  requestParam: ICreatePackageResponse,
  useCpr: boolean
) => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.GET, props.config, 'wso2');
  const useApi = useCpr ? props.config.wso2OpenApiUrl : props.config.wso2ApiUrl;
  const quoteIDParamString = getQuotesParamGenerator(requestParam);
  const query: string = `brand=${requestParam.brand}&productkey=${requestParam.productKey}&${quoteIDParamString}`;
  return fetch(`${useApi}/packages?${query}`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.wso2OpenApiUrl}/packages?${query}`, err, props, dispatch);
    }
  );
};

// Send email to Customer service
export const sendEmail = (props: IAppProps, dispatch: TDispatch, payload: ISalesCompleteEmail) => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.POST, props.config, 'wso2', payload);
  return fetch(`${props.config.wso2OpenApiUrl}/email`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.wso2OpenApiUrl}/email`, err, props, dispatch);
    }
  );
};

export const sendCallMeEmail = (context: IAppProps, dispatch: TDispatch, payload: IGeneralCallMeEmailTemplate) => {
  const options = getDefaultOptions(HTTP_METHOD.POST, context.config, 'wso2', payload);
  return fetch(`${context.config.wso2OpenApiUrl}/email`, options).then(
    (response) => handleHTTPErrors(response, context, dispatch, options),
    (err) => {
      handleGeneralErrors(`${context.config.wso2OpenApiUrl}/email`, err, context, dispatch);
    }
  );
};

// Persist data to cache and return key string
export const sendDataToObjectCache = async (
  props: IAppProps,
  dispatch: TDispatch,
  data: any
): Promise<{ objectId: string }> => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.POST, props.config, 'objectCache', data);
  return fetch(`${props.config.objectCacheUrl}`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(props.config.objectCacheUrl, err, props, dispatch);
    }
  );
};

// Get data from object cache
export const getDataFromObjectCache = async (props: IAppProps, dispatch: TDispatch, cacheId: string) => {
  const options = getDefaultOptions(HTTP_METHOD.GET, props.config, 'objectCache');
  return fetch(`${props.config.objectCacheUrl}/${cacheId}`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.objectCacheUrl}/${cacheId}`, err, props, dispatch);
    }
  );
};

// Buy Insurance
export const buyInsurance = (props: IAppProps, dispatch: TDispatch, data: IBuyInsurance) => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.PUT, props.config, 'wso2Secured', data);
  return fetch(`${props.config.wso2ApiUrl}/packages/buy`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(`${props.config.wso2ApiUrl}/packages/buy`, err, props, dispatch);
    }
  );
};

export const doEboksSubscription = (props: IAppProps, dispatch: TDispatch, customerId: string) => {
  dispatch({ isLoading: true });
  const options = getDefaultOptions(HTTP_METHOD.GET, props.config, 'wso2Secured');
  return fetch(`${props.config.apiGatewayBase}/v2/GTEboks/GTEboks/subscribe?brand=TD&id=${customerId}`, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(
        `${props.config.apiGatewayBase}/v2/GTEboks/GTEboks/subscribe?brand=TD&id=${customerId}`,
        err,
        props,
        dispatch
      );
    }
  );
};

type BankInfo = {
  regNumber: string;
  accNumber: string;
};
export const checkBankInfo = async (props: IAppProps, dispatch: TDispatch, bankInfo: BankInfo) => {
  const options = getDefaultOptions(HTTP_METHOD.POST, props.config, 'wso2Secured', {
    regNo: bankInfo.regNumber,
    accountNo: bankInfo.accNumber
  });
  const environment = window.topContext.config.getSiteConfig().environment;
  options.headers = { ...options.headers, 'x-top-dscm': environment };
  const url = `${props.config.apiGatewayBase}/v2/openapi/automated-payment-service/payment/services/bankaccount/validator`;
  return fetch(url, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      handleGeneralErrors(url, err, props, dispatch);
    }
  );
};

export const fetchGiftCardDetails = async (props: IAppProps, dispatch: TDispatch) => {
  const options = getDefaultOptions(HTTP_METHOD.GET, props.config, 'wso2');
  const url = `${props.config.apiGatewayBase}/v2/openapi/onlinesales-services/api/products/giftCard?key=travel`;

  return fetch(url, options).then(
    (response) => handleHTTPErrors(response, props, dispatch, options),
    (err) => {
      dispatch({
        giftCard: {
          status: 'inactive',
          amount: '0',
          key: PRODUCTKEY.REJSE,
          campaign: 'default'
        }
      });
      handleGeneralErrors(url, err, props, dispatch);
    }
  );
};
